import React, { useEffect, useRef, useState } from 'react'
import './badges-form.styles.scss';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import ManageContentInformation from '../blocks/information.component';
import ManageContentVisibility from '../blocks/visibility.component';
import ManageContentIconCropped from '../blocks/icon.component';
import ManageContentCategories from '../blocks/categories.component';
import { useSelector } from 'react-redux';
import { getDownloadURL, getStorage, ref, uploadBytes } from 'firebase/storage';
import { UseCloudFunction } from '@/utils/firebase.utils';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import ManageBadgePurpose from '../blocks/badge-purpose.component';
import { RandomString } from '@/utils/randomString';
import UpgradeRequiredModal from '@/components/SHARED/upgrade-required-modal/upgrade-required-modal.component';
import { membershipRestrictions, testMemberRestrictions } from '@/utils/membershipRestrictions';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import ElementDelete from '@/components/SHARED/element-delete/element-delete.component';
import ShowHideSettingsButton from '../blocks/show-hide-button.component';
import useAnalytics from '@/utils/useAnalytics';
import { getOptimizedBlob } from '@/utils/getOptimizedBlob';
import ManageContentRewards from '../blocks/rewards.component';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import { useEffectiveMembership } from '@/utils/useEffectiveMembership';

const BadgesForm = ({
  gameElements,
  gameStats,
  badgesFromDb,
  gameData,
  role }) => {

  const screenSize = useSelector(state => state.contentData.screenSize);
  const membership = useEffectiveMembership();
  const userMembership = useSelector(state => state.userData.membership);
  const userProfile = useSelector(state => state.userData.userProfile);
  const [formData, setFormData] = useState({});
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [existingDescription, setExistingDescription] = useState(null);
  const [clearEditor, setClearEditor] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { badgePath } = useParams();
  const { trackEvent, trackException } = useAnalytics();
  const dataRef = useRef();
  const approveRef = useRef();
  const accessDeniedRef = useRef(null);
  const navigate = useNavigate();
  const storage = getStorage();
  const location = useLocation();

  useEffect(() => {
    if (!role || !gameData?.path) return;
    if (role !== 'manager' && role !== 'owner') {
      setAlertMessage('You do not have permission to create or edit badges.');
      accessDeniedRef.current = () => navigate(`/manage/${gameData.path}/elements/badges`);
    }
  }, [role, gameData?.path, navigate])

  useEffect(() => {
    if (!gameData || !membership || !location) return;
    if (location.pathname.includes('/elements/badges/edit')) return;
    const res = testMemberRestrictions({
      'gameData': gameData,
      'membership': membership,
      'create': true
    });
    if (res.disabled && res.disabledReason.includes('elements')) {
      const planRest = membershipRestrictions[membership.membership];
      const message = `While using the ${planRest.title}, you are only able to have ${planRest.elementCount} active Game Elements per game.`;
      setShowUpgradeModal(message);
      return;
    }
  }, [gameData, membership, location])

  useEffect(() => {
    if (!badgePath || !badgesFromDb) {
      dataRef.current = {
        'opt_visible': true,
        'opt_available': true,
        'opt_preview': true,
        'opt_purposeGroup': false,
      }
      setFormData(dataRef.current);
      return;
    }
    dataRef.current = Object.values(badgesFromDb)
      .filter(e => e.path === badgePath)[0];
    setFormData(dataRef.current);
    // console.log(dataRef.current);
    if (dataRef.current.desc) {
      setExistingDescription(dataRef.current.desc);
    }
  }, [badgePath, badgesFromDb])

  useEffect(() => {
    if (!badgesFromDb) { return; }
    let arr = [];
    for (let c of Object.values(badgesFromDb).filter(c => c.type === 'badge')) {
      if (c.opt_cat && !arr.includes(c.opt_cat)) {
        arr.push(c.opt_cat)
      }
    }
    setCategories(arr.sort())
  }, [badgesFromDb])

  function saveData(key, value) {
    dataRef.current = { ...dataRef.current, ...{ [key]: value } };
    setFormData(dataRef.current);
    console.log(dataRef.current);
  }

  function cancelCreate() {
    setConfirmMessage('Are you sure you want to cancel? All of your data will be lost.');
    approveRef.current = () => {
      dataRef.current = {};
      setFormData(dataRef.current);
      navigate(-1)
    }
  }

  async function handleSubmit() {
    if (location.pathname.includes('/preview/manager')) {
      setAlertMessage('Sorry, data cannot be saved in preview mode!');
      return;
    }
    let tempData = { ...dataRef.current }
    console.log(tempData);
    const reqs = ['name', 'icon', 'opt_purposeGroup']
    for (let r of reqs) {
      if (tempData[r] === undefined || tempData[r] === null || tempData[r] === '') {
        setAlertMessage('Please include a name, an icon, and a purpose for this badge before saving.');
        return;
      }
    }
    console.log(Object.values(gameElements).filter(e =>
      e.name.trim().toLowerCase() === tempData.name.trim().toLowerCase() &&
      e.id !== tempData.id
    ));
    if (
      gameElements &&
      Object.values(gameElements).filter(e =>
        e.name.trim().toLowerCase() === tempData.name.trim().toLowerCase() &&
        e.id !== tempData.id
      ).length > 0
    ) {
      setAlertMessage('There is already a Badge with this name. ' +
        'Please choose a different name for this badge.');
      setLoading(false);
      return;
    }
    setConfirmMessage('Are you sure you are ready to save this badge? All of these settings can be changed later.')
    approveRef.current = () => {
      finishApproval()
    }
  }

  async function finishApproval() {
    setLoading(true);
    let tempData = { ...dataRef.current }
    // console.log(tempData);
    if (tempData.icon.upload) {
      // tempData.icon.croppie.result({
      // 	type:'blob',
      // 	size: {'width': 252, 'height': 252},
      //     quality: 0.5,
      // })
      // .then((blob) => {
      // 	uploadImage(tempData.icon, blob)
      // 	return;
      // })
      const optimizedBlob = await getOptimizedBlob(tempData.icon.croppie, 25);
      console.log('Cropped image size in KB:', optimizedBlob.size / 1024);
      uploadImage(tempData.icon, optimizedBlob);
    } else if (tempData.icon.url) {
      saveBadge(tempData.icon.url)
      return;
    } else if (tempData.icon) {
      saveBadge(tempData.icon)
      return;
    } else {
      setAlertMessage('Something went wrong with the icon. Please try using a different image.');
      trackException('Badge Save Error: icon error.');
      setLoading(false);
      return;
    }
  }

  function uploadImage(icon, blob) {
    const now = new Date().getTime();
    const path = `${userProfile.appId}/${now}-${icon.name}`;
    const iconRef = ref(storage, path)
    const metadata = {
      contentType: icon.type,
      customMetadata: {
        appId: userProfile.appId,
        email: userProfile.email
      }
    };
    uploadBytes(iconRef, blob, metadata).then((snapshot) => {
      getDownloadURL(iconRef)
        .then(async (url) => {
          saveBadge(url);
          try {
            await UseCloudFunction(
              'saveToMyContent',
              {
                'gameId': gameData.gameId,
                'appId': userProfile.appId,
                'source': 'Badge Icons',
                'category': 'Images',
                'cropped': true,
                'contentData': {
                  'name': icon.name,
                  'url': url,
                  'type': 'image',
                  'id': `${now}_${RandomString(10)}_image`
                },
              }
            )
          } catch (error) {
            console.log('Error saving to My Content: ', error);
            trackException('Badge Icon Save to My Content Error: ' + error.message);
          }
          trackEvent(
            'image_saved_to_my_content',
            'Game Manager',
            `Badge icon ${icon.name} was uploaded for user ${userProfile.appId}.`,
            null,
            userMembership
          )
          return;
        })
    })
  }

  async function saveBadge(iconUrl) {
    let tempData = { ...dataRef.current }
    console.log(tempData);
    const now = new Date().getTime();
    const gameCode = gameData.gameId.substring(gameData.gameId.indexOf('-') - 5, gameData.gameId.indexOf('-') + 6)
    let action = 'Edited';
    if (!tempData.id) {
      tempData.appId = userProfile.appId;
      tempData.gameId = gameData.gameId;
      tempData.id = now + '-' + gameCode + '-badge';
      tempData.status = 'active';
      tempData.type = 'badge';
      tempData.path = RandomString(18, 'abcdefghijklmnopqrstuvwxyz-_0123456789');
      tempData.opt_available = true;
      action = 'Created';
    }
    tempData.icon = iconUrl;
    console.log(tempData);
    const res = await UseCloudFunction(
      'saveGameElement',
      {
        'gameId': gameData.gameId,
        'appId': userProfile.appId,
        'elementData': tempData,
        'userName': `${userProfile.fName} ${userProfile.lName}`,
        'action': action
      }
    )
    console.log(res);
    if (res.error) {
      setAlertMessage('Something went wrong. Error: ' + res.error);
      setLoading(false);
      trackException(`Badge ${action === 'Created' ? 'creation' : 'edit'} Error: ${res.error}`);
      return;
    }
    setAlertMessage('Badge saved successfully.');
    dataRef.current = {};
    setFormData(dataRef.current);
    setLoading(false);
    const event = action === 'Created' ? 'badge_created' : 'badge_edited';
    trackEvent(
      event,
      'Game Manager',
      `Badge ${tempData.id} was ${action === 'Created' ? 'created' : 'edited'}.`,
      null,
      userMembership
    )
    navigate(-1);
  }

  return (
    <div className={`create-badge-form ${screenSize}`}>
      <div className='form-grid'>
        <div className='g-card badges-card'>
          <BackButton cancel={() => navigate(-1)} />
          <div className="g-space-1"></div>
          <div className='card-title'>
            <ImageContainer src='/images/icons/badges.png' alt='badges icon' className='head-icon' />
            Create and Edit Badges
          </div>
          <hr />
          <div className='badges-landing-content'>
            <span className='required meta'>* = Required</span>
            <div className="g-space-0-5"></div>
            <ManageContentInformation
              type='badge'
              saveData={saveData}
              formData={formData}
              existingDescription={existingDescription}
              clearEditor={clearEditor}
              setClearEditor={setClearEditor}
              textEditorLabel='Badge Description (optional)'
            />
            <div className='g-space-2'></div>
            <ManageContentIconCropped
              type='badge'
              saveData={saveData}
              formData={formData}
            />
            {/* <div className='g-space-2'></div>
            <ManageBadgePurpose
              saveData={saveData}
              formData={formData}
            /> */}
            <div className="g-space-1"></div>
            <ShowHideSettingsButton
              showAdvancedOptions={showAdvancedOptions}
              setShowAdvancedOptions={setShowAdvancedOptions} />
            <div className='g-space-1'></div>
            <div className={`advanced-options ${showAdvancedOptions ? 'show' : 'hide'}`}>
              <ManageContentCategories
                type='badge'
                saveData={saveData}
                formData={formData}
                categories={categories}
              />
              <div className='g-space-2'></div>
              <ManageContentVisibility
                type='badge'
                saveData={saveData}
                formData={formData}
                previewText='Allow players to see this badge before they can earn it. (If toggled off, players will see a mystery badge.)'
                visibilityText="Turn visibility ON for this Badge. If toggled off, it will be totally hidden from players regardless of whether or not they have earned it."
                availabilityText="This badge can be earned through an achievement or a manager-distributed reward. If toggled off, the badge cannot be earned in any way."
              />
              <div className="g-space-1"></div>
              <ManageContentRewards
                type='badge'
                gameData={gameData}
                gameStats={gameStats}
                saveData={saveData}
                formData={formData}
                gameElements={gameElements}
                text="this Badge"
              />
              <div className="g-space-1"></div>
              <ShowHideSettingsButton
                showAdvancedOptions={showAdvancedOptions}
                setShowAdvancedOptions={setShowAdvancedOptions}
                below={true} />
            </div>
            <div className='buttons'>
              {
                (loading)
                  ?
                  <>
                    {
                      (formData.ts_created) &&
                      <button
                        type='button'
                        className={`g-button small-btn red ${screenSize !== 'mobile' ? 'left-item' : ''}`}>
                        Delete
                      </button>
                    }
                    <button
                      type='button'
                      className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}>
                      Cancel
                    </button>
                    <button
                      type='button'
                      className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}>
                      <ThreeDotsLoader />
                    </button>
                  </>
                  :
                  <>
                    {
                      (formData.ts_created) &&
                      <ElementDelete
                        element={formData}
                        style='button'
                        classText={screenSize !== 'mobile' ? 'left-item' : ''} />
                    }
                    <button
                      type='button'
                      className={`g-button ${screenSize === 'mobile' ? 'med-btn' : ''}`}
                      onClick={() => cancelCreate()}>
                      Cancel
                    </button>
                    <button
                      type='button'
                      className={`g-button submit-button`}
                      onClick={() => handleSubmit()}>
                      Save Badge
                    </button>
                  </>
              }
            </div>
          </div>
        </div>
        {
          (screenSize !== 'mobile' && screenSize !== 'tablet') &&
          <div >
            <div className='g-card fixed-card'>
              <button
                className='g-button med-btn submit-button'
                onClick={() => handleSubmit()}
              >
                Save Badge
              </button>
            </div>
          </div>
        }
      </div>
      <ModalAlert
        show={alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage}
        onApprove={accessDeniedRef.current} />
      <ModalConfirm show={confirmMessage} cancel={() => setConfirmMessage(null)} message={confirmMessage} onApprove={approveRef.current} />
      <UpgradeRequiredModal
        show={showUpgradeModal}
        cancel={() => setShowUpgradeModal(null)}
        message={showUpgradeModal}
      />
      <FullPageLoader show={loading} />
    </div>
  )
}

export default BadgesForm